import { GsFormField, GsFormIf } from '../../../components';
import { Color, GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth } from '../../../models';

export interface GsBorderSchema {
  borderWidth?: GsStructBorderWidth;
  borderStyle?: GsStructBorderStyle;
  borderColor?: Color;
  borderRadius?: GsStructBorderRadius;
}

/**
 * Generates a schema for form field configurations for border properties.
 *
 * @param suffix (It should start with a capital letter) that could append to the form control names.
 * @param defaults Optional default values for the border properties.
 * @param groupingName Optional name for grouping the fields in the form.
 * @param ifArgs Optional conditions for displaying the fields.
 * @param translationRoot Optional translation root for labels. (default: 'schema.border'; appends 'Color', 'Width', 'Style', etc. to this root)
 * @returns a schema for form field configurations for border width, style, and color.
 *
 * The returned SCHEMA includes the following fields:
 * - `borderWidth`: A select field for border width with options for 0px, 1px, 2px, 4px, 8px, 10px, 12px, 14px, 16px, 18px, and 20px.
 * - `borderStyle`: A select field for border style with options for None, Solid, Dotted, Dashed, and Double.
 * - `borderColor`: A color field for border color with a default value of 'primary-main'.
 */
export function borderSchemaFields(
  suffix = '',
  defaults?: GsBorderSchema,
  groupingName?: string | false,
  ifArgs?: GsFormIf,
  translationRoot: string = 'schema.border'
): GsFormField<never>[] {
  const resolvedGroupingName = groupingName === false ? undefined : (groupingName ?? 'schema.border.name' + suffix);

  /** Condition: borderStyle is not 'none' — hides irrelevant fields when no border is active */
  const hasBorderStyleIf: GsFormIf = {
    args: [
      ...(ifArgs?.args ?? []),
      { formControlName: 'borderStyle' + suffix, value: GsStructBorderStyle.none, operator: '!=' }
    ],
    chaining: 'and'
  };

  return [
    {
      formControlName: 'borderStyle' + suffix,
      label: translationRoot + '.borderStyle',
      type: 'select',
      if: ifArgs,
      valueType: 'number',
      groupingName: resolvedGroupingName,
      default: defaults?.borderStyle ?? GsStructBorderStyle.none,
      options: [
        { text: 'schema.border.style.none', value: GsStructBorderStyle.none },
        { text: 'schema.border.style.solid', value: GsStructBorderStyle.solid },
        { text: 'schema.border.style.dotted', value: GsStructBorderStyle.dotted },
        { text: 'schema.border.style.dashed', value: GsStructBorderStyle.dashed },
        { text: 'schema.border.style.double', value: GsStructBorderStyle.double }
      ]
    },
    {
      formControlName: 'borderWidth' + suffix,
      label: translationRoot + '.borderWidth',
      type: 'select',
      valueType: 'number',
      if: hasBorderStyleIf,
      groupingName: resolvedGroupingName,
      default: defaults?.borderWidth ?? GsStructBorderWidth.zeroPixel,
      options: [
        { text: '0px', value: GsStructBorderWidth.zeroPixel },
        { text: '1px', value: GsStructBorderWidth.onePixel },
        { text: '2px', value: GsStructBorderWidth.twoPixels },
        { text: '4px', value: GsStructBorderWidth.fourPixels },
        { text: '8px', value: GsStructBorderWidth.eightPixels },
        { text: '10px', value: GsStructBorderWidth.tenPixels },
        { text: '12px', value: GsStructBorderWidth.twelvePixels },
        { text: '14px', value: GsStructBorderWidth.fourteenPixels },
        { text: '16px', value: GsStructBorderWidth.sixteenPixels },
        { text: '18px', value: GsStructBorderWidth.eighteenPixels },
        { text: '20px', value: GsStructBorderWidth.twentyPixels }
      ]
    },
    {
      formControlName: 'borderColor' + suffix,
      label: translationRoot + '.borderColor',
      if: hasBorderStyleIf,
      type: 'color',
      groupingName: resolvedGroupingName,
      default: defaults?.borderColor ?? 'primary-main'
    },
    ...borderRadiusSchemaFields(suffix, defaults?.borderRadius, resolvedGroupingName, hasBorderStyleIf, translationRoot)
  ];
}

export function borderRadiusSchemaFields(
  suffix = '',
  defaultValue?: GsStructBorderRadius,
  groupingName?: string | false,
  ifArgs?: GsFormIf,
  translationRoot: string = 'schema.border',
  isResponsive: boolean = false
): GsFormField<never>[] {
  const resolvedGroupingName = groupingName === false ? undefined : (groupingName ?? 'schema.border.name' + suffix);
  const options = [
    { text: 'schema.border.radius.none', value: GsStructBorderRadius.none },
    { text: 'schema.border.radius.small', value: GsStructBorderRadius.sm },
    { text: 'schema.border.radius.regular', value: GsStructBorderRadius.regular },
    { text: 'schema.border.radius.medium', value: GsStructBorderRadius.md },
    { text: 'schema.border.radius.large', value: GsStructBorderRadius.lg },
    { text: 'schema.border.radius.xlarge', value: GsStructBorderRadius.xl },
    { text: 'schema.border.radius.xxlarge', value: GsStructBorderRadius.xxl },
    { text: 'schema.border.radius.xxxlarge', value: GsStructBorderRadius.xxxl },
    { text: 'schema.border.radius.full', value: GsStructBorderRadius.full }
  ];
  if (isResponsive) {
    return [
      {
        formControlName: 'borderRadius' + suffix,
        label: translationRoot + '.borderRadius',
        type: 'select',
        valueType: 'number',
        if: ifArgs,
        groupingName: resolvedGroupingName,
        isResponsive: true,
        default: { default: defaultValue ?? GsStructBorderRadius.none },
        options
      }
    ];
  }
  return [
    {
      formControlName: 'borderRadius' + suffix,
      label: translationRoot + '.borderRadius',
      type: 'select',
      valueType: 'number',
      if: ifArgs,
      groupingName: resolvedGroupingName,
      default: defaultValue ?? GsStructBorderRadius.none,
      options
    }
  ];
}
